import React from 'react';
import { SOFT, SOFT_HOVER, SOLID, FOCUS_RING, normaliseTone, cx } from './toneStyles';

/**
 * Pill — small rounded status/label chip.
 *
 * Props
 *   tone     see toneStyles (neutral | primary | warm | success | warning | danger | feed | …)
 *   size     'sm' (default, text-xs) | 'md' (text-sm)
 *   icon     Md icon component (aria-hidden)
 *   dot      leading status dot in the tone's solid colour
 *   live     dot + motion-safe pulse (running timers / in-progress states)
 *   as       'span' (default) | 'button' — button gets a 44px hit box via a pseudo element
 *   onClick  with as='button'
 *
 * Give button pills an aria-label when the visible text is abbreviated ("Asleep · 42 m").
 */
const SIZES = {
  sm: 'text-xs px-2 py-0.5 min-h-[22px] gap-1',
  md: 'text-sm px-2.5 py-1 min-h-[28px] gap-1.5',
};
const ICON = { sm: 'w-3.5 h-3.5', md: 'w-4 h-4' };

export default function Pill({
  children,
  tone = 'neutral',
  size = 'sm',
  icon: Icon,
  dot = false,
  live = false,
  as = 'span',
  onClick,
  className = '',
  ...props
}) {
  const t = normaliseTone(tone);
  const isButton = as === 'button';
  const Tag = isButton ? 'button' : as || 'span';
  const dotCls = SOLID[t].split(' ')[0];

  return (
    <Tag
      type={isButton ? 'button' : undefined}
      onClick={onClick}
      className={cx(
        'inline-flex items-center rounded-full font-medium whitespace-nowrap',
        SIZES[size] || SIZES.sm,
        SOFT[t],
        isButton &&
          cx(
            'relative before:absolute before:content-[""] before:-inset-y-3 before:-inset-x-1',
            SOFT_HOVER[t],
            'active:scale-[0.98] transition-[background-color,transform] duration-fast ease-out',
            FOCUS_RING
          ),
        className
      )}
      {...props}
    >
      {(dot || live) && (
        <span
          aria-hidden="true"
          className={cx('inline-block w-1.5 h-1.5 rounded-full flex-shrink-0', dotCls, live && 'motion-safe:animate-pulseSoft')}
        />
      )}
      {Icon && <Icon className={cx(ICON[size] || ICON.sm, 'flex-shrink-0')} aria-hidden="true" />}
      <span className="truncate">{children}</span>
    </Tag>
  );
}
